import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton';
import GhostButton from '@wix/da-ds/pkg/Buttons/GhostButton';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';

import { BiData, BiContextProvidedValue, MessageIconClickBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useRestrictToLoggedInAndVerified } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedInAndVerified';
import { Url } from '@wix/da-url';

interface Props {
  className?: string;
  profileOwner: PapiUser;
  isContactable?: boolean;
  iconOnly?: boolean;
}

export const SendMessageButton: FC<Props> = ({
  className,
  profileOwner,
  isContactable = true,
  iconOnly = false,
}) => {
  const { restrictToLoggedInAndVerified, isLoggedIn, isVerificationNeeded } =
    useRestrictToLoggedInAndVerified();
  const { t } = useTranslation();

  const handleClick = restrictToLoggedInAndVerified(ReferralType.MESSAGES, 'messages', undefined);

  const shouldBlockAction = isVerificationNeeded || !isLoggedIn;
  const messagesHref = shouldBlockAction ? undefined : Url.userMessageLink(profileOwner.username);

  const biData = BiData<MessageIconClickBiEvent>({
    evid: 470,
    recipientid: profileOwner.useridUuid,
    sectionname: BiContextProvidedValue,
  });

  if (iconOnly) {
    return (
      <IconButton
        size="medium"
        className={className}
        tooltipText={t(`navigation.subnavigation.sendMessage`)}
        icon={MailIcon}
        href={messagesHref}
        onClick={shouldBlockAction ? handleClick : undefined}
        aria-label={t('navigation.subnavigation.sendMessage')}
        biData={biData}
      />
    );
  }

  return (
    <GhostButton
      size="medium"
      className={className}
      variant="main"
      href={messagesHref}
      onClick={shouldBlockAction ? handleClick : undefined}
      aria-label={t('navigation.subnavigation.sendMessage')}
      biData={biData}
    >
      {t(`navigation.subnavigation.sendMessage`)}
    </GhostButton>
  );
};
SendMessageButton.displayName = 'SendMessageButton';

export default SendMessageButton;
